import React, { useState, useEffect } from "react";
import {
	PaymentRequestButtonElement,
	useStripe,
} from "@stripe/react-stripe-js";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Typography from "@material-ui/core/Typography";

import styled from "styled-components";
import CreditCardForm from "./CreditCardForm";
import dictionary from "../../utils/dictionary";
import Receipt from "./Receipt";

const acceptableLanguages = ["en", "fr", "es"];

const ButtonContainer = styled.div`
	padding-left: 8px;
	padding-right: 8px;
`;

const ELEMENT_OPTIONS = {
	style: {
		paymentRequestButton: {
			type: "buy",
			theme: "dark",
		},
	},
};

const useStyles = makeStyles({
	root: (props) => ({
		background: props.background,
		"&:hover": {
			background: props.background,
		},
		"&:focus": {
			background: props.background,
		},
		transition: "none",
		border: 0,
		borderRadius: 4,
		color: "white",
		height: 40,
		padding: "0 30px",
		textTransform: "none",
		fontSize: "16px",
		marginTop: "12px",
		fontWeight: "normal",
	}),
	sub: () => ({
		background: "#fff",
		"&:hover": {
			background: "#fff",
		},
		"&:focus": {
			background: "#fff",
		},
		color: "#000",
		transition: "none",
		border: 0,
		borderRadius: 4,
		height: 40,
		padding: "0 30px",
		textTransform: "none",
		fontSize: "16px",
		marginTop: "12px",
		fontWeight: "normal",
	}),
});

const PaymentButtons = ({
	amount,
	businessName,
	country,
	currency,
	brandColor,
	clientSecret,
	language,
	_invoice,
	invoiceData,
}) => {
	// set checkout form button color
	const props = { background: brandColor || "#3D4050" };
	const classes = useStyles(props);
	const stripe = useStripe();

	const [success, setSuccess] = useState(null);
	const [paymentRequest, setPaymentRequest] = useState(null);
	const [usingCreditCard, setCreditCard] = useState(false);
	const [loading, setLoading] = useState(true);
	const [disabled, setDisabled] = useState(false);

	const {
		paymentSuccessTitle,
		paymentSuccessBody,
		back,
		creditCard,
	} = dictionary[acceptableLanguages.includes(language) ? language : "en"];

	const CHEKKIT_GREEN = "#03B856";

	// for apple, google, microsoft pay
	useEffect(() => {
		if (stripe) {
			const pr = stripe.paymentRequest({
				country,
				currency,
				total: {
					label: businessName,
					amount,
				},
				requestPayerName: true,
				requestPayerEmail: true,
			});
			// Check the availability of the Payment Request API.
			if (pr)
				pr.canMakePayment().then((result) => {
					if (result) {
						// handle submission of the payment request button (apple pay, google pay, microsoft pay)
						pr.on("paymentmethod", async (ev) => {
							// disable pay by credit card button
							setDisabled(true);

							// Confirm the PaymentIntent without handling potential next actions (yet).
							const {
								error: confirmError,
							} = await stripe.confirmCardPayment(
								clientSecret,
								{ payment_method: ev.paymentMethod.id },
								{ handleActions: false }
							);

							if (confirmError) {
								// Report to the browser that the payment failed, prompting it to
								// re-show the payment interface, or show an error message and close
								// the payment interface.
								ev.complete("fail");
								setDisabled(false);
							} else {
								// Report to the browser that the confirmation was successful, prompting
								// it to close the browser payment method collection interface.
								ev.complete("success");

								// Let Stripe.js handle the rest of the payment flow.
								const {
									error,
									paymentIntent,
								} = await stripe.confirmCardPayment(
									clientSecret
								);

								if (error) {
									console.log("paymentRequestError", {
										error,
									});
									// The payment failed -- ask your customer for a new payment method.
								} else if (
									paymentIntent.status === "succeeded"
								) {
									// The payment has succeeded.
									setSuccess(
										paymentSuccessTitle + paymentSuccessBody
									);
								}
							}
						});

						setPaymentRequest(pr);
					}
					setLoading(false);
				});
		}
	}, [stripe]);

	// before apple pay has loaded, load empty div
	if (loading) {
		return <div />;
	}

	if (success) {
		return (
			<div>
				<Typography style={{ marginBottom: "15px" }}>
					<h5 style={{ color: CHEKKIT_GREEN }}>
						{paymentSuccessTitle}{" "}
					</h5>
					{paymentSuccessBody}
				</Typography>
				<Receipt
					invoiceData={invoiceData}
					language={language}
					_invoice={_invoice}
					currency={currency}
				/>
			</div>
		);
	}

	// when user successfully submits form on checkout, render download receipt button
	const onCheckoutSubmit = ({ success: checkoutSuccess }) => {
		if (checkoutSuccess) {
			setSuccess(true);
		}
	};
	if (usingCreditCard) {
		return (
			<ButtonContainer>
				<CreditCardForm
					currency={currency}
					amount={amount}
					brandColor={brandColor}
					clientSecret={clientSecret}
					disabled={disabled}
					onCheckoutSubmit={onCheckoutSubmit}
					language={language}
				/>
				<Button
					variant="contained"
					fullWidth
					className={classes.sub}
					disableElevation
					onClick={() => setCreditCard(false)}
				>
					<ArrowBackIcon /> {back}
				</Button>
			</ButtonContainer>
		);
	}

	// payment request button for google pay, apple pay, microsoft pay
	if (paymentRequest) {
		return (
			<ButtonContainer>
				<PaymentRequestButtonElement
					options={{ ...ELEMENT_OPTIONS, paymentRequest }}
				/>

				<Button
					variant="contained"
					fullWidth
					disableElevation
					className={classes.root}
					onClick={() => setCreditCard(true)}
				>
					{creditCard}
				</Button>
			</ButtonContainer>
		);
	}

	return (
		<ButtonContainer>
			<Button
				variant="contained"
				fullWidth
				disableElevation
				className={classes.root}
				onClick={() => setCreditCard(true)}
			>
				{creditCard}
			</Button>
		</ButtonContainer>
	);
};

export default PaymentButtons;



// WEBPACK FOOTER //
// ./src/components/payments/PaymentButtons.js